import type { SystemTableColumn } from './tableColumn';

export type SortDirection = 'asc' | 'desc';

export type SortValue = string | number | Date | null | undefined;

export type SortConfig<K extends string> = {
    key: K;
    dir: SortDirection;
};

type SortableColumn<SortKey extends string, T> = {
    key: SortKey;
    sortable: true;
    sortValue?: (row: T) => SortValue;
};

type SortableSystemTableColumn<K extends string, SortKey extends K, T> = Extract<
    SystemTableColumn<K, SortKey, T>,
    { sortable: true }
>;

const getSortValue = <T, SortKey extends string>(row: T, column: SortableColumn<SortKey, T>): SortValue => {
    if (column.sortValue) {
        return column.sortValue(row);
    }

    return (row as Record<string, SortValue>)[column.key];
};

const isMissingSortValue = (value: SortValue) =>
    value === null || value === undefined || value === '' || (value instanceof Date && Number.isNaN(value.getTime()));

const compareSortValues = (a: SortValue, b: SortValue): number => {
    if (a instanceof Date || b instanceof Date) {
        return new Date(a ?? '').getTime() - new Date(b ?? '').getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        sensitivity: 'base',
    });
};

export const getNextSortConfig = <K extends string>(prev: SortConfig<K> | null, key: K): SortConfig<K> => {
    if (prev?.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    }

    return { key, dir: 'asc' };
};

export const sortItems = <T, K extends string, SortKey extends K>(
    items: T[],
    sortConfig: SortConfig<SortKey> | null,
    columns: readonly SystemTableColumn<K, SortKey, T>[]
): T[] => {
    if (!sortConfig) return items;

    const column = columns.find(
        (column): column is SortableSystemTableColumn<K, SortKey, T> =>
            column.key === sortConfig.key && column.sortable === true
    );

    if (!column) return items;

    return [...items].sort((a, b) => {
        const aValue = getSortValue(a, column);
        const bValue = getSortValue(b, column);

        const aMissing = isMissingSortValue(aValue);
        const bMissing = isMissingSortValue(bValue);

        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;

        const comparison = compareSortValues(aValue, bValue);

        return sortConfig.dir === 'asc' ? comparison : -comparison;
    });
};
